/**
 * Widget Data Fetching Utility
 * Implementation of IWidgetDataFetcher
 * Handles client-side fetching of widget data from PL API
 *
 * Uses strict types from @lastbrand/storefront-proto-registry for type safety
 */

import { WidgetType } from '@lastbrand/storefront-proto-registry/api/v1/enums'

import { PLBaseWidgetProps } from '@src/components/pl-base-widget'
import { PLPageMeta } from '@src/components/pl-page-context'
import { API_PRESENTATION_LAYER_SERVICE } from '@src/config/env'
import { LOCALE_STANDARD_FORMAT } from '@src/config/localisation/locale-config.constants'
import { SVD_ENRICH_VERSION_HEADER, SVD_ENRICH_VERSION_VALUE } from '@src/config/svd-flags'
import {
  PERSONALISATION_ENABLED_HEADER,
  REGION_ID_HEADER,
  REGION_LANG_HEADER,
  SUBSCRIPTION_EXP_ENABLED_HEADER,
} from '@src/constants/api.headers.constants'
import { HTTP_HEADERS } from '@src/constants/common.constants'
import { CONTENT_VERSION_HEADER } from '@src/constants/content-version.constants'
import { PL_API_WIDGET_FETCH_DATA } from '@src/constants/pl-api.constants'
import { FEATURE_HEADERS } from '@src/quince-site-common/src/utils/constants'
import { getContentEnvMode, getContentProductsEnv } from '@src/quince-site-common/src/utils/getEnvironmentParam'
import { getlbUserId } from '@src/quince-site-common/src/utils/getlb-user-id'
import isBrowser from '@src/quince-site-common/src/utils/isBrowser'
import { getPersonalisationAllowedFromCookies } from '@src/quince-site-common/src/utils/personalisation-consent'
import { getContentVersionOnClient, getLocalisationHeaders } from '@src/utils/general.utils'
import { safeDecodeSlug } from '@src/utils/slug.utils'

import { IWidgetDataFetcher } from '../contracts/data-fetching'

import { resolvePLRequestSlug } from './resolve-pl-request-slug'
import { getPlatformType } from './resolve-platform-type'
import { buildProductListRequestParams } from './widget-request-builders'

import type { Device, Pagination, UserContext } from '@lastbrand/storefront-proto-registry/api/v1/common'
import type { WidgetFetchRequest, WidgetFetchResponse } from '@lastbrand/storefront-proto-registry/api/v1/widgetfetch'

/**
 * Get widget pagination endpoint URL
 * Uses API_PRESENTATION_LAYER_SERVICE which already contains the base URL
 */
function getWidgetPaginationEndpoint(): string {
  return `${API_PRESENTATION_LAYER_SERVICE}${PL_API_WIDGET_FETCH_DATA}`
}

/**
 * Implementation of IWidgetDataFetcher
 */
export class WidgetDataFetchingService implements IWidgetDataFetcher {
  private readonly endpoint: string
  private readonly pageMeta: PLPageMeta
  private readonly svdEnrichHeaderEnabled: boolean

  // `svdEnrichHeaderEnabled` asks PL for SVD-shaped cards on this pagination request (STRFRNT-15810:
  // SVD-flow users page from a backend that returns SVD-shaped cards so page 2+ stays SVD-shaped,
  // matching the SSR first page). See `SVD_ENRICH_VERSION_HEADER` doc for current environment status.
  constructor(_widgetProps: PLBaseWidgetProps, pageMeta: PLPageMeta, svdEnrichHeaderEnabled = false) {
    this.endpoint = getWidgetPaginationEndpoint()
    this.pageMeta = pageMeta
    this.svdEnrichHeaderEnabled = svdEnrichHeaderEnabled
  }

  async fetchWidgetData(widgetId: string, pagination?: Pagination): Promise<WidgetFetchResponse> {
    // Get locale and regionId from client-side headers
    const localisationHeaders = getLocalisationHeaders()
    const regionId = localisationHeaders[REGION_ID_HEADER]
      ? Number.parseInt(localisationHeaders[REGION_ID_HEADER], 10)
      : 101 // Default to 101 (US) if not available
    const locale = localisationHeaders[REGION_LANG_HEADER] ?? LOCALE_STANDARD_FORMAT

    // Path-based subcollection routes must keep PL requests anchored to the base collection slug.
    // Query/base routes retain the older pathname fallback for backward compatibility.
    let slug = resolvePLRequestSlug(this.pageMeta, { requestType: 'widget' })
    slug = safeDecodeSlug(slug)

    // Build Device info - use SSR platformType if available, fallback to client detection
    const device: Device = {
      platform: getPlatformType(this.pageMeta.platformType),
      // Optional fields (only for native app): version, buildNumber, deviceId, osVersion, os
    }

    // Build UserContext - use SSR lbUserId if available, fallback to cookie.
    // `||` (not `??`) so an empty-string cookie read also falls through — PL 400s on a
    // blank user.lbUserId. Same sentinel as fetch-layout-data until the root cause lands.
    const user: UserContext = {
      // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
      lbUserId: this.pageMeta.lbUserId || getlbUserId() || 'fallback_lb_user_id',
      zipcode: this.pageMeta.zipCode ?? undefined,
    }

    // Shared with the v2 lazy batch: sorting + v2-aware filters + path-mode subcollection.
    const productListRequestParams = buildProductListRequestParams(this.pageMeta, pagination)

    // Build request payload using WidgetFetchRequest type from proto registry
    const requestPayload: WidgetFetchRequest = {
      id: widgetId,
      slug,
      device,
      type: WidgetType.PRODUCT_LIST,
      user,
      request: {
        productList: productListRequestParams,
      },
    }

    // Construct headers
    const contentVersion = getContentVersionOnClient()
    const headers: Record<string, string> = {
      [HTTP_HEADERS.CONTENT_TYPE]: HTTP_HEADERS.APPLICATION_JSON,
      accept: HTTP_HEADERS.APPLICATION_JSON,
      'x-gctx-request-origin-region-id': String(regionId),
      'x-gctx-request-origin-locale': locale,
      [FEATURE_HEADERS.CONTENT_ENV]: getContentProductsEnv(),
      [FEATURE_HEADERS.CONTENT_ENV_MODE]: getContentEnvMode(),
      [SUBSCRIPTION_EXP_ENABLED_HEADER]: this.pageMeta.subscriptionExpEnabled ? 'true' : 'false',
      // Forward the user's functional-consent decision (UK opt-in / US-CA opt-out).
      [PERSONALISATION_ENABLED_HEADER]: getPersonalisationAllowedFromCookies() ? 'true' : 'false',
      ...(contentVersion ? { [CONTENT_VERSION_HEADER]: contentVersion } : {}),
      ...(this.svdEnrichHeaderEnabled ? { [SVD_ENRICH_VERSION_HEADER]: SVD_ENRICH_VERSION_VALUE } : {}),
      ...localisationHeaders,
    }

    try {
      if (!isBrowser()) {
        throw new Error('Widget pagination API calls are only supported client-side')
      }

      // Client-side fetch using fetch API
      const response = await fetch(this.endpoint, {
        method: 'POST',
        headers,
        body: JSON.stringify(requestPayload),
      })

      if (!response.ok) {
        // Try to get error details from response body
        let errorDetails = ''
        try {
          const errorBody = await response.text()
          errorDetails = errorBody ? ` - ${errorBody}` : ''
          console.error('Widget data fetch failed', {
            status: response.status,
            statusText: response.statusText,
            errorBody,
            requestPayload,
          })
        } catch {
          // Ignore parsing errors
        }
        throw new Error(`Widget data fetch failed: ${response.status} ${response.statusText}${errorDetails}`)
      }

      return (await response.json()) as WidgetFetchResponse
    } catch (error) {
      console.error('Widget Data Fetch Failed', {
        widgetId,
        pagination,
        endpoint: this.endpoint,
        error: error instanceof Error ? error.message : String(error),
      })
      throw error
    }
  }
}

/**
 * Create widget data fetching service instance
 * Factory function that creates an IWidgetDataFetcher implementation
 * for client-side widget pagination API calls
 *
 * @param widgetProps - Widget props containing context, pagination, experiments, etc.
 * @param pageMeta - Page metadata containing pathname, searchParams, seoData, etc.
 * @returns IWidgetDataFetcher instance ready to use with useWidgetPagination hook
 */
export function createWidgetDataFetchingService(
  widgetProps: PLBaseWidgetProps,
  pageMeta: PLPageMeta,
  svdEnrichHeaderEnabled?: boolean,
): IWidgetDataFetcher {
  return new WidgetDataFetchingService(widgetProps, pageMeta, svdEnrichHeaderEnabled)
}
